import type { Permission } from "@backchannel/tax-contracts";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  stringifySearchWith,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { NavIcon } from "~/components/layout/nav-icon";
import { Page } from "~/components/layout/page";
import { useT } from "~/i18n";
import { hasPermission } from "~/lib/access/permissions";
import { useSession } from "~/lib/access/session-context";
import { productCatalogSearchSchema } from "~/lib/url/product-catalog-search";
import {
  agentFindingsSearchSchema,
  auditSearchSchema,
  autoApprovalSearchSchema,
  browserAgentSearchSchema,
  calculationSearchSchema,
  coverageGapSearchSchema,
  evidenceSearchSchema,
  fiscalTaskSearchSchema,
  fiscalVerificationSearchSchema,
  invoicesSearchSchema,
  lotSimulatorSearchSchema,
  lotsSearchSchema,
  overviewSearchSchema,
  parseAppSearch,
  parseSearchValue,
  proposalSearchSchema,
  ruleSearchSchema,
  rulesetSearchSchema,
  sessionReplaySearchSchema,
  sessionsSearchSchema,
  settingsSearchSchema,
  sourceRunSearchSchema,
  sourceSearchSchema,
  uploadFailuresSearchSchema,
  webResearchSearchSchema,
} from "~/lib/url/search";
import { RootLayout } from "./root-layout";

/**
 * Code-based route tree with `lazyRouteComponent` on every leaf.
 *
 * The initial bundle carries the shell only: router, query client, i18n,
 * design tokens. A triager who lives in `/evidence` never downloads the
 * ruleset diff viewer or the calculation explain drawer.
 */
const rootRoute = createRootRoute({
  component: Outlet,
  errorComponent: lazyRouteComponent(() => import("~/routes/root-error"), "RootErrorRoute"),
  notFoundComponent: lazyRouteComponent(() => import("~/routes/not-found"), "NotFoundRoute"),
});

/**
 * Boundary for every calculator surface (individual, Compra e venda, workbook,
 * lot history and detail, research): a crash or a failed route shows what
 * happened and what to do, never a blank screen.
 */
const calculatorErrorComponent = lazyRouteComponent(
  () => import("~/routes/calculations/calculator-route-error"),
  "CalculatorRouteError",
);

/** Outside the session gate: invite, reset and leftover callback links. */
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: lazyRouteComponent(() => import("~/routes/auth/callback"), "AuthCallbackRoute"),
});

const authInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/invite",
  component: lazyRouteComponent(() => import("~/routes/auth/invite"), "AuthInviteRoute"),
});

const authResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/reset",
  component: lazyRouteComponent(() => import("~/routes/auth/reset"), "AuthResetRoute"),
});

/** Pathless layout: everything below it requires an authenticated session. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: PermissionLandingRoute,
});

/**
 * A principal can be authenticated without being allowed into every part of
 * the control plane. Resolve the root in-app using the same permissions as
 * the sidebar and the API, so a normal news operator never lands on a 403
 * overview page after login.
 */
export const LANDING_DESTINATIONS = [
  { to: "/overview", permission: "READ:TAX_OVERVIEW:ALL" },
  { to: "/evidence", permission: "READ:TAX_NEWS:ALL" },
  { to: "/proposals", permission: "READ:TAX_RULE:ALL" },
  { to: "/calculations/simulate", permission: "READ:TAX_CALCULATION:ALL" },
] as const satisfies ReadonlyArray<{ to: string; permission: Permission }>;

export function landingPathForPermissions(permissions: readonly Permission[]): string {
  return (
    LANDING_DESTINATIONS.find(({ permission }) => hasPermission(permissions, permission))?.to ??
    "/settings"
  );
}

function PermissionLandingRoute() {
  const t = useT();
  const { permissions, effectiveRole } = useSession();
  const navigate = useNavigate();
  const target =
    effectiveRole === "TAX_COMMERCIAL"
      ? "/commercial"
      : effectiveRole === "TAX_DEVELOPER"
        ? "/settings?workspace=developers"
        : landingPathForPermissions(permissions);

  useEffect(() => {
    if (effectiveRole === "TAX_DEVELOPER") {
      void navigate({
        to: "/settings",
        search: { workspace: "developers" },
        hash: "integrations",
        replace: true,
      } as never);
      return;
    }
    void navigate({ to: target as never, replace: true });
  }, [effectiveRole, navigate, target]);

  return (
    <Page mark={<NavIcon name="overview" />} title={t("common.loading")}>
      <output className="text-xs text-muted" aria-live="polite">
        {t("common.loading")}
      </output>
    </Page>
  );
}

const commercialRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/commercial",
  component: lazyRouteComponent(
    () => import("~/routes/overview/commercial-home"),
    "CommercialHomeRoute",
  ),
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/overview",
  validateSearch: overviewSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/overview/overview"), "OverviewRoute"),
});

const evidenceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/evidence",
  validateSearch: evidenceSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/evidence/evidence-list"),
    "EvidenceListRoute",
  ),
});

const workbookRowDraftReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/evidence/workbook-review",
  validateSearch: (search: Record<string, unknown>) => ({
    extractId:
      typeof search.extractId === "string" &&
      /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
        search.extractId,
      )
        ? search.extractId
        : undefined,
  }),
  component: lazyRouteComponent(
    () => import("~/routes/evidence/workbook-row-draft-review"),
    "WorkbookRowDraftReviewRoute",
  ),
});

const evidenceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/evidence/$evidenceId",
  component: lazyRouteComponent(
    () => import("~/routes/evidence/evidence-detail"),
    "EvidenceDetailRoute",
  ),
});

const proposalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/proposals",
  validateSearch: proposalSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/proposals/proposal-list"),
    "ProposalListRoute",
  ),
});

const proposalPlanImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/proposals/import",
  component: lazyRouteComponent(
    () => import("~/routes/proposals/proposal-plan-import"),
    "ProposalPlanImportRoute",
  ),
});

const proposalDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/proposals/$proposalId",
  component: lazyRouteComponent(
    () => import("~/routes/proposals/proposal-detail"),
    "ProposalDetailRoute",
  ),
});

const rulesetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rulesets",
  validateSearch: rulesetSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/rulesets/ruleset-list"), "RulesetListRoute"),
});

const rulesetDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rulesets/$rulesetId",
  validateSearch: rulesetSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/rulesets/ruleset-detail"),
    "RulesetDetailRoute",
  ),
});

const rulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules",
  validateSearch: ruleSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/rules/ncm-quick-lookup"),
    "NcmQuickLookupRoute",
  ),
});

const ruleLibraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/library",
  validateSearch: ruleSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/rules/rule-list"), "RuleListRoute"),
});
/**
 * Catálogo NCM is "Pesquisa por produto" inside Regras → Consultar. Old links
 * (bookmarks, command search) keep working: the catalog query becomes a
 * product search and an exact NCM opens its lookup result.
 */
const ncmCatalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/ncm",
  validateSearch: ruleSearchSchema,
  beforeLoad: ({ search }) => {
    const exact = search.ncm && /^\d{8}$/.test(search.ncm) ? search.ncm : undefined;
    throw redirect({
      to: "/rules",
      search: exact
        ? {
            by: "ncm",
            ncm: exact,
            originUf: search.originUf,
            destinationUf: search.destinationUf,
            asOf: search.asOf,
          }
        : {
            by: "product",
            q: search.q ?? search.ncm,
            chapter: search.chapter,
            originUf: search.originUf,
            destinationUf: search.destinationUf,
            asOf: search.asOf,
          },
      replace: true,
    });
  },
});

const newRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/new",
  component: lazyRouteComponent(() => import("~/routes/rules/new-rules"), "NewRulesRoute"),
});

const coverageGapsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/gaps",
  validateSearch: coverageGapSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/rules/coverage-gaps"), "CoverageGapsRoute"),
});

const fiscalVerificationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/verification",
  validateSearch: fiscalVerificationSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/rules/fiscal-verification"),
    "FiscalVerificationRoute",
  ),
});

const ruleCoverageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/coverage",
  component: lazyRouteComponent(() => import("~/routes/rules/coverage"), "RuleCoverageRoute"),
});

const ruleChapterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/chapters/$chapter",
  component: lazyRouteComponent(
    () => import("~/routes/rules/rule-chapter-detail"),
    "RuleChapterDetailRoute",
  ),
});

const ruleRouteGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/guide",
  component: lazyRouteComponent(() => import("~/routes/rules/route-guide"), "RuleRouteGuideRoute"),
});

const ruleDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rules/$ruleId",
  validateSearch: ruleSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/rules/rule-detail"), "RuleDetailRoute"),
});

const calculationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations",
  errorComponent: calculatorErrorComponent,
  validateSearch: calculationSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/calculation-list"),
    "CalculationListRoute",
  ),
});

const calculationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations/$calculationId",
  errorComponent: calculatorErrorComponent,
  validateSearch: calculationSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/calculation-detail"),
    "CalculationDetailRoute",
  ),
});

const lotSimulatorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations/simulate",
  errorComponent: calculatorErrorComponent,
  staticData: { layout: "calculator-focus" },
  validateSearch: lotSimulatorSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/lot-runtime-simulator"),
    "LotRuntimeSimulatorRoute",
  ),
});

const fiscalTasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations/tasks",
  errorComponent: calculatorErrorComponent,
  validateSearch: fiscalTaskSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/fiscal-task-list"),
    "FiscalTaskListRoute",
  ),
});

const fiscalTaskDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations/tasks/$taskId",
  errorComponent: calculatorErrorComponent,
  validateSearch: fiscalTaskSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/fiscal-task-detail"),
    "FiscalTaskDetailRoute",
  ),
});

/** Calculadora → Pesquisas: every "Buscar com agente?" request, with filters. */
const researchJobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calculations/research",
  errorComponent: calculatorErrorComponent,
  component: lazyRouteComponent(
    () => import("~/routes/calculations/research-jobs"),
    "ResearchJobsRoute",
  ),
});

/** Lot history: every calculation job with what it returned, for audit. */
const lotsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lots",
  errorComponent: calculatorErrorComponent,
  validateSearch: lotsSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/lots/lot-history"), "LotHistoryRoute"),
});

/** Notas fiscais: the tax manager's NF-e workspace (upload, months, findings, validation). */
const invoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/invoices",
  validateSearch: invoicesSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/invoices/invoices-route"), "InvoicesRoute"),
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products",
  validateSearch: productCatalogSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/products/product-catalog"),
    "ProductCatalogRoute",
  ),
});

const sourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sources",
  validateSearch: sourceSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/sources/source-list"), "SourceListRoute"),
});

const sourceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sources/$sourceId",
  validateSearch: sourceSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/sources/source-detail"),
    "SourceDetailRoute",
  ),
});

const sourceRunDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sources/$sourceId/runs/$runId",
  validateSearch: sourceRunSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/sources/source-run-detail"),
    "SourceRunDetailRoute",
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  validateSearch: auditSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/audit/audit-list"), "AuditListRoute"),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit/$auditId",
  component: lazyRouteComponent(() => import("~/routes/audit/audit-detail"), "AuditDetailRoute"),
});

const DEVELOPER_SETTINGS_HASHES = new Set(["integrations", "api-docs", "mcp", "requests"]);

/**
 * `/settings` keeps the Developers workspace (and its old `#section` deep
 * links). The personal account moved to `/account`, one page with sections,
 * so the shell keeps the operations rail instead of swapping it.
 */
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  validateSearch: settingsSearchSchema,
  beforeLoad: ({ search, location }) => {
    const hash = location.hash.replace(/^#/, "");
    if (search.workspace === "developers" || DEVELOPER_SETTINGS_HASHES.has(hash)) return;
    throw redirect({ to: "/account", hash: "", replace: true } as never);
  },
  component: lazyRouteComponent(() => import("~/routes/settings/settings"), "SettingsRoute"),
});

/** Minha conta: Perfil · Idioma · Passkeys · Senha on one page. */
const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("~/routes/settings/account-settings"), "AccountRoute"),
});

const accountPreferencesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/preferences",
  beforeLoad: () => {
    throw redirect({ to: "/account", hash: "language", replace: true } as never);
  },
});

const accountPasskeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/passkeys",
  beforeLoad: () => {
    throw redirect({ to: "/account", hash: "passkeys", replace: true } as never);
  },
});

const accountPasswordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/password",
  beforeLoad: () => {
    throw redirect({ to: "/account", hash: "password", replace: true } as never);
  },
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: lazyRouteComponent(() => import("~/routes/users/users"), "UsersRoute"),
});

const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/agents",
  validateSearch: browserAgentSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/admin/agents"), "AgentsRoute"),
});

const webResearchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/web-research",
  validateSearch: webResearchSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/admin/web-research"), "WebResearchRoute"),
});

const demosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/demos",
  component: lazyRouteComponent(() => import("~/routes/admin/demos"), "DemosRoute"),
});

const demoDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/demos/$demoId",
  component: lazyRouteComponent(() => import("~/routes/admin/demos"), "DemoDetailRoute"),
});

const howItWorksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/how-it-works",
  component: lazyRouteComponent(() => import("~/routes/admin/how-it-works"), "HowItWorksRoute"),
});

const agentFindingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/agent-findings",
  validateSearch: agentFindingsSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/admin/agent-findings"),
    "AgentFindingsRoute",
  ),
});

/** Administração → Planilhas com erro: failed calculator uploads, by support code. */
const uploadFailuresRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/upload-failures",
  validateSearch: uploadFailuresSearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/admin/upload-failures"),
    "UploadFailuresRoute",
  ),
});

/** Administração → Sessões: session replay and the operator activity log. */
const sessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/sessions",
  validateSearch: sessionsSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/admin/sessions"), "SessionsRoute"),
});

const sessionReplayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/sessions/$replaySessionId",
  validateSearch: sessionReplaySearchSchema,
  component: lazyRouteComponent(
    () => import("~/routes/admin/session-replay"),
    "SessionReplayRoute",
  ),
});

/** Administração → Custo estimado da infraestrutura: the monthly GCP estimate. */
const infraCostRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/infra-cost",
  component: lazyRouteComponent(() => import("~/routes/admin/infra-cost"), "InfraCostRoute"),
});

/** Administração → Autoaprovação: governed auto-approval pipeline (ADR 0020). */
const autoApprovalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/auto-approval",
  validateSearch: autoApprovalSearchSchema,
  component: lazyRouteComponent(() => import("~/routes/admin/auto-approval"), "AutoApprovalRoute"),
});

const legalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/legal",
  component: lazyRouteComponent(() => import("~/routes/legal/legal"), "LegalRoute"),
});

export const routeTree = rootRoute.addChildren([
  authCallbackRoute,
  authInviteRoute,
  authResetRoute,
  appRoute.addChildren([
    indexRoute,
    commercialRoute,
    overviewRoute,
    evidenceRoute,
    workbookRowDraftReviewRoute,
    evidenceDetailRoute,
    proposalsRoute,
    proposalPlanImportRoute,
    proposalDetailRoute,
    rulesetsRoute,
    rulesetDetailRoute,
    rulesRoute,
    ruleLibraryRoute,
    ncmCatalogRoute,
    newRulesRoute,
    coverageGapsRoute,
    fiscalVerificationRoute,
    ruleCoverageRoute,
    ruleChapterRoute,
    ruleRouteGuideRoute,
    ruleDetailRoute,
    calculationsRoute,
    calculationDetailRoute,
    lotSimulatorRoute,
    fiscalTasksRoute,
    fiscalTaskDetailRoute,
    researchJobsRoute,
    lotsRoute,
    invoicesRoute,
    productsRoute,
    sourcesRoute,
    sourceDetailRoute,
    sourceRunDetailRoute,
    auditRoute,
    auditDetailRoute,
    settingsRoute,
    accountRoute,
    accountPreferencesRoute,
    accountPasskeysRoute,
    accountPasswordRoute,
    usersRoute,
    agentsRoute,
    webResearchRoute,
    demosRoute,
    demoDetailRoute,
    howItWorksRoute,
    agentFindingsRoute,
    uploadFailuresRoute,
    sessionsRoute,
    sessionReplayRoute,
    infraCostRoute,
    autoApprovalRoute,
    legalRoute,
  ]),
]);

export function createAppRouter() {
  return createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 10_000,
    // Search params are the app's state store; keep them stable across renders
    // so query keys do not churn.
    defaultStructuralSharing: true,
    parseSearch: parseAppSearch,
    stringifySearch: stringifySearchWith(JSON.stringify, parseSearchValue),
  });
}

export const router = createAppRouter();

export const routes = {
  commercial: commercialRoute,
  overview: overviewRoute,
  evidence: evidenceRoute,
  workbookRowDraftReview: workbookRowDraftReviewRoute,
  evidenceDetail: evidenceDetailRoute,
  proposals: proposalsRoute,
  proposalPlanImport: proposalPlanImportRoute,
  proposalDetail: proposalDetailRoute,
  rulesets: rulesetsRoute,
  rulesetDetail: rulesetDetailRoute,
  rules: rulesRoute,
  ruleLibrary: ruleLibraryRoute,
  ncmCatalog: ncmCatalogRoute,
  newRules: newRulesRoute,
  coverageGaps: coverageGapsRoute,
  fiscalVerification: fiscalVerificationRoute,
  ruleCoverage: ruleCoverageRoute,
  ruleChapter: ruleChapterRoute,
  ruleRouteGuide: ruleRouteGuideRoute,
  ruleDetail: ruleDetailRoute,
  calculations: calculationsRoute,
  calculationDetail: calculationDetailRoute,
  lotSimulator: lotSimulatorRoute,
  fiscalTasks: fiscalTasksRoute,
  fiscalTaskDetail: fiscalTaskDetailRoute,
  researchJobs: researchJobsRoute,
  lots: lotsRoute,
  invoices: invoicesRoute,
  products: productsRoute,
  sources: sourcesRoute,
  sourceDetail: sourceDetailRoute,
  sourceRunDetail: sourceRunDetailRoute,
  audit: auditRoute,
  auditDetail: auditDetailRoute,
  agents: agentsRoute,
  webResearch: webResearchRoute,
  demos: demosRoute,
  demoDetail: demoDetailRoute,
  agentFindings: agentFindingsRoute,
  uploadFailures: uploadFailuresRoute,
  sessions: sessionsRoute,
  sessionReplay: sessionReplayRoute,
  infraCost: infraCostRoute,
  autoApproval: autoApprovalRoute,
};

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
